import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

/**
 * Global keyboard shortcut handler.
 * - n        → open new lesson dialog
 * - g d      → go to /
 * - g t      → go to /takvim
 * - g o      → go to /ogrenciler
 * - g m      → go to /muhasebe
 * - g r      → go to /raporlar
 * - g a      → go to /ayarlar
 * - ?        → show help dialog (open/close via callback)
 *
 * Ignores events when user is typing in input/textarea/select/contenteditable.
 */
export default function useKeyboardShortcuts({ onNewLesson, onShowHelp } = {}) {
  const nav = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let gPending = false;
    let gTimer = null;

    function isEditable(el) {
      if (!el) return false;
      const tag = (el.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return true;
      if (el.isContentEditable) return true;
      return false;
    }

    function onKey(e) {
      // Ignore if user is typing in a field or any modifier is held
      if (isEditable(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const k = e.key.toLowerCase();

      // ? → help
      if (e.key === "?" || (e.shiftKey && e.key === "/")) {
        e.preventDefault();
        onShowHelp?.();
        return;
      }

      // g prefix handling
      if (gPending) {
        const map = {
          d: "/",
          t: "/takvim",
          o: "/ogrenciler",
          m: "/muhasebe",
          r: "/raporlar",
          a: "/ayarlar",
        };
        if (map[k]) {
          e.preventDefault();
          nav(map[k]);
        }
        gPending = false;
        clearTimeout(gTimer);
        return;
      }
      if (k === "g") {
        gPending = true;
        gTimer = setTimeout(() => { gPending = false; }, 1200);
        return;
      }

      // n → new lesson (only on pages where it makes sense)
      if (k === "n") {
        if (location.pathname === "/" || location.pathname.startsWith("/takvim")) {
          e.preventDefault();
          onNewLesson?.();
        }
      }
    }

    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      clearTimeout(gTimer);
    };
  }, [nav, location.pathname, onNewLesson, onShowHelp]);
}
